import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import { useNamedItemMap } from '../../../useNamedItemMap';
import AddIcon from '@material-ui/icons/Add';
import LambdaIcon from '../../../../assets/aws/lambda.svg';
import type { ValkyrieLambda, ValkyrieLambdaDefaults } from 'config-editor-common';
import { ValkyrieDefaultsEditor } from './ValkyrieDefaultsEditor';
import { ValkyrieLambdaItemEditor } from './ValkyrieLambdaItemEditor';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  icon: {
    width: '1.5rem',
    height: '1.5rem',
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  addAnotherButton: {
    marginTop: theme.spacing(2),
  },
}));

interface ValkyrieLambdaListEditorProps {
  lambdas: Record<string, ValkyrieLambda>;
  defaults?: ValkyrieLambdaDefaults;
  onDefaultsChange?: (defaults: ValkyrieLambdaDefaults) => void;
  onChange: (lambdas: Record<string, ValkyrieLambda>) => void;
  /** When false, hide the defaults section (e.g. when defaults are in a separate scaffolder field) */
  showDefaults?: boolean;
  /** Queue names for sqs_queue_urls selector (from sqs_queues) */
  queueNames?: string[];
}

function newEmptyLambda(): ValkyrieLambda {
  return {
    lambda_handler: '',
    lambda_trigger_path: '',
  };
}

export const ValkyrieLambdaListEditor = ({
  lambdas,
  defaults,
  onDefaultsChange,
  onChange,
  showDefaults = true,
  queueNames = [],
}: ValkyrieLambdaListEditorProps) => {
  const classes = useStyles();
  const { add, update, remove, rename, registerRef } = useNamedItemMap<ValkyrieLambda>(
    lambdas,
    onChange,
    newEmptyLambda,
  );

  return (
    <div className={classes.section}>
      <Box className={classes.header}>
        <div className={classes.titleContainer}>
          <img src={LambdaIcon} alt="Lambda" className={classes.icon} />
          <Typography variant="h6">Endpoint Lambdas</Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={add}
          startIcon={<AddIcon />}
          style={{ backgroundColor: '#FF9900', color: '#FFFFFF' }}
        >
          Add Lambda
        </Button>
      </Box>

      {showDefaults && onDefaultsChange && (
        <ValkyrieDefaultsEditor defaults={defaults} onChange={onDefaultsChange} />
      )}

      {Object.keys(lambdas).length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">No endpoint lambdas configured</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Add an endpoint lambda to handle API requests.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={add}
            style={{
              marginTop: 16,
              backgroundColor: '#FF9900',
              color: '#FFFFFF',
            }}
          >
            Add First Lambda
          </Button>
        </div>
      ) : (
        <>
          {Object.entries(lambdas).map(([lambdaName, lambda]) => (
            <ValkyrieLambdaItemEditor
              key={lambdaName}
              ref={registerRef(lambdaName)}
              name={lambdaName}
              lambda={lambda}
              onUpdate={update}
              onRename={rename}
              onDelete={remove}
              queueNames={queueNames}
            />
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={add}
            className={classes.addAnotherButton}
          >
            Add another lambda
          </Button>
        </>
      )}
    </div>
  );
};
